import clsx from 'clsx';
import React, { useState } from 'react';
import { FiChevronRight, FiChevronLeft, FiChevronDown } from 'react-icons/fi';

export interface DataTableColumn<T = Record<string, unknown>> {
  key: string;
  label: string;
  width?: string;
  render?: (value: unknown, row: T) => React.ReactNode;
}

export interface DataTableProps<T = Record<string, unknown>> {
  columns: DataTableColumn<T>[];
  data: T[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  className?: string;
}

export const DataTable: React.FC<DataTableProps> = ({
  columns,
  data,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  className,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const itemsPerPageOptions = [10, 15, 25, 50];

  const handleItemsPerPageChange = (value: number) => {
    if (onItemsPerPageChange) {
      onItemsPerPageChange(value);
    }
    setIsDropdownOpen(false);
  };

  return (
    <div className={clsx('bg-white rounded-lg border border-neutral-200', className)}>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-neutral-200">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="px-6 py-4 text-left text-sm font-semibold text-neutral-800"
                  style={{ width: column.width }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr
                key={index}
                className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors"
              >
                {columns.map((column) => (
                  <td key={column.key} className="px-6 py-4 text-sm text-neutral-700">
                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-end px-6 py-4 border-t border-neutral-200">
        <div className="relative flex gap-2 items-center">
          <button
            type="button"
            onClick={() => { setIsDropdownOpen(!isDropdownOpen); }}
            className="flex items-center gap-2 text-sm text-purple-600 font-medium hover:text-purple-700 transition-colors"
          >
            Quantidade por página
            <FiChevronDown className="w-4 h-4" />
          </button>
          
          {isDropdownOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => { setIsDropdownOpen(false); }}                onKeyDown={(e) => {
                  if (e.key === 'Escape') setIsDropdownOpen(false);
                }}
                role="button"
                tabIndex={0}
                aria-label="Fechar dropdown"              />
              <div className="absolute bottom-full left-0 mb-2 bg-white border border-neutral-200 rounded-lg shadow-lg py-2 z-20 min-w-20">
                {itemsPerPageOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => { handleItemsPerPageChange(option); }}
                    className={clsx(
                      'w-full px-4 py-2 text-center text-sm hover:bg-purple-50 transition-colors',
                      option === itemsPerPage && 'bg-purple-100 text-purple-700 font-medium'
                    )}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </>
          )}
          
          <div className="text-sm text-neutral-600 mt-1">
            {startItem}-{endItem} de {totalItems}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => { onPageChange(currentPage - 1); }}
            disabled={currentPage === 1}
            className="p-1 text-neutral-600 hover:text-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Página anterior"
          >
            <FiChevronLeft className="w-5 h-5" />
          </button>

          <span className="text-sm text-neutral-700 font-medium min-w-15 text-center">
            {currentPage} de {totalPages}
          </span>

          <button
            type="button"
            onClick={() => { onPageChange(currentPage + 1); }}
            disabled={currentPage === totalPages}
            className="p-1 text-neutral-600 hover:text-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Próxima página"
          >
            <FiChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};
